import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dashGetAnalytics, dashGetDrilldown } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';
const BLUE   = '#0176CE';

function pct(num, den) {
  if (!den || den === 0) return 0;
  return Math.round((num / den) * 100);
}

function fmtDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function initials(first, last, company) {
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (company) return company.slice(0, 2).toUpperCase();
  return '??';
}

const AVATAR_COLORS = [NAVY, BLUE, ORANGE, '#059669', '#7c3aed', '#0891b2', '#b45309', '#dc2626'];
function avatarColor(id) { return AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length]; }

function StatCard({ label, value, sub, color = BLUE, topColor }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
      borderTop: `4px solid ${topColor || color}`,
      padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color, letterSpacing: '-0.5px', marginBottom: 2 }}>{value ?? '—'}</div>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      {sub && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function RateBar({ value, max = 100, color = BLUE }) {
  const w = Math.min(100, Math.round((value / Math.max(max, 1)) * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#f0f5fb', borderRadius: 3, overflow: 'hidden', maxWidth: 80 }}>
        <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 34, textAlign: 'right' }}>{value}%</span>
    </div>
  );
}

function Badge({ children, color = BLUE }) {
  const bg = color === '#dc2626' ? 'rgba(239,68,68,0.08)' :
             color === '#059669' ? 'rgba(5,150,105,0.08)' :
             color === ORANGE    ? 'rgba(243,116,40,0.08)' :
             color === '#b45309' ? 'rgba(180,83,9,0.08)' :
                                   'rgba(1,118,206,0.08)';
  return (
    <span style={{ background: bg, color, borderRadius: 5, fontSize: 10, fontWeight: 600,
                   padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

const DRILL_LABELS = {
  bounce: 'Hard Bounces',
  spam:   'Spam Complaints',
  unsub:  'Unsubscribes',
  halted: 'Halted Contacts',
};

function DrilldownModal({ type, onClose }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    dashGetDrilldown(type)
      .then(r => { setRows(r.contacts || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [type]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,37,64,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 680, maxHeight: '80vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid #e2e8f0' }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: NAVY }}>{DRILL_LABELS[type] || type}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 32, color: '#64748b', textAlign: 'center' }}>Loading…</div>}
          {error   && <div style={{ padding: 32, color: '#dc2626', textAlign: 'center' }}>{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 32, color: '#94a3b8', textAlign: 'center' }}>No records found</div>
          )}
          {!loading && !error && rows.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Contact', 'Email', 'Company', 'Date'].map(h => (
                    <th key={h} style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '9px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <tr key={c.contact_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: NAVY }}>
                      <Link to={`/admin/b2b/contacts/${c.contact_id}`} style={{ color: NAVY, textDecoration: 'none' }} onClick={onClose}>
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                      </Link>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b' }}>{c.email}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151' }}>
                      {[c.company_name, c.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(c.event_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{rows.length} contact{rows.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}

export default function DashAnalytics() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [drillType, setDrillType] = useState(null);

  const openDrill = useCallback(type => setDrillType(type), []);
  const closeDrill = useCallback(() => setDrillType(null), []);

  useEffect(() => {
    dashGetAnalytics()
      .then(r => { setData(r); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={s.page}>
      <AdminSidebar active="dash-analytics" />
      <main style={s.main}><div style={{ color: '#64748b', padding: 40 }}>Loading analytics…</div></main>
    </div>
  );

  if (error) return (
    <div style={s.page}>
      <AdminSidebar active="dash-analytics" />
      <main style={s.main}><div style={{ color: '#dc2626', padding: 40 }}>{error}</div></main>
    </div>
  );

  const { kpi, jobs, templates, contacts, companies, activity, schedule, pipeline } = data;
  const openRate  = pct(kpi.total_opens,  kpi.total_sent);
  const clickRate = pct(kpi.total_clicks, kpi.total_sent);
  const bounceRate = pct(kpi.total_bounces + kpi.total_oob, kpi.total_sent);
  const deliverRate = kpi.total_delivered > 0 ? pct(kpi.total_delivered, kpi.total_sent) : 100;

  // Build pipeline map: step -> count
  const pipeMap = {};
  (pipeline || []).forEach(r => { pipeMap[r.current_step] = r.cnt; });
  const maxStep = Math.max(...(templates || []).map(t => t.step_number || 0).filter(Boolean), 5);

  // Hot leads: contacts with clicks >= 3
  const hotLeads = (contacts || []).filter(c => c.clicks >= 3).slice(0, 3);

  return (
    <div style={s.page}>
      <AdminSidebar active="dash-analytics" />
      {drillType && <DrilldownModal type={drillType} onClose={closeDrill} />}
      <main style={s.main}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={s.title}>Dash Analytics</h1>
            <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
              Live marketing performance · all time
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/admin/dash" style={{ ...s.outlineBtn, textDecoration: 'none' }}>← Back to Dash</Link>
            <button style={s.primaryBtn} onClick={() => { setLoading(true); dashGetAnalytics().then(r => { setData(r); setLoading(false); }); }}>
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* ── Row 1: Top KPIs ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 16 }}>
          <StatCard label="Active Jobs"       value={kpi.active_jobs}      sub="Running streams"           color={NAVY}   topColor={NAVY} />
          <StatCard label="In Pipeline"       value={kpi.active_contacts}  sub={`+${kpi.available_contacts?.toLocaleString()} available`} color={BLUE}   topColor={BLUE} />
          <StatCard label="Emails Sent"       value={kpi.total_sent?.toLocaleString()} sub="All time"     color={ORANGE} topColor={ORANGE} />
          <StatCard label="Open Rate"         value={`${openRate}%`}       sub={`${kpi.total_opens} opens`}  color={BLUE}  topColor={BLUE} />
          <StatCard label="Click Rate"        value={`${clickRate}%`}      sub={`${kpi.total_clicks} clicks`} color={ORANGE} topColor={ORANGE} />
        </div>

        {/* ── Row 2: Deliverability KPIs ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          <div style={{ cursor: 'pointer' }} onClick={() => openDrill('bounce')} title="Click to see affected contacts">
            <StatCard label="Hard Bounces"    value={kpi.total_bounces + kpi.total_oob}
                      sub={bounceRate === 0 ? '✓ Clean list · click to view' : `${bounceRate}% bounce rate · click to view`}
                      color={kpi.total_bounces + kpi.total_oob > 0 ? '#dc2626' : '#059669'}
                      topColor={kpi.total_bounces + kpi.total_oob > 0 ? '#dc2626' : '#059669'} />
          </div>
          <div style={{ cursor: 'pointer' }} onClick={() => openDrill('spam')} title="Click to see affected contacts">
            <StatCard label="Spam Complaints" value={kpi.total_spam}
                      sub={kpi.total_spam === 0 ? '✓ 0.0% rate · click to view' : `${pct(kpi.total_spam, kpi.total_sent)}% of sent · click to view`}
                      color={kpi.total_spam > 0 ? '#dc2626' : '#059669'}
                      topColor={kpi.total_spam > 0 ? '#dc2626' : '#059669'} />
          </div>
          <div style={{ cursor: 'pointer' }} onClick={() => openDrill('unsub')} title="Click to see affected contacts">
            <StatCard label="Unsubscribes"    value={kpi.total_unsubs}
                      sub={`${pct(kpi.total_unsubs, kpi.total_sent)}% of sent · click to view`}
                      color={kpi.total_unsubs > 5 ? '#b45309' : '#64748b'}
                      topColor={kpi.total_unsubs > 5 ? '#b45309' : '#e2e8f0'} />
          </div>
          <div style={{ cursor: 'pointer' }} onClick={() => openDrill('halted')} title="Click to see affected contacts">
            <StatCard label="Completed"       value={kpi.completed_contacts} sub={`${kpi.halted_contacts} halted · click to view`}
                      color="#059669" topColor="#059669" />
          </div>
        </div>

        {/* ── Row 3: Active Jobs + Deliverability Health ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 18, marginBottom: 18 }}>

          {/* Active Jobs */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Active Marketing Jobs</span>
              <Link to="/admin/b2b/jobs" style={s.linkBtn}>Manage jobs →</Link>
            </div>
            <div style={{ padding: '8px 0' }}>
              {(jobs || []).length === 0 && <div style={s.empty}>No active jobs</div>}
              {(jobs || []).map(j => {
                const jobOpenRate  = pct(j.open_count,  j.sent_count);
                const jobClickRate = pct(j.click_count, j.sent_count);
                return (
                  <div key={j.job_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.job_name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{j.stream_name} · {j.send_days || 'Mon–Fri'} · {j.send_hour_start || '?'}–{j.send_hour_end || '?'} UTC</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 44 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>{j.active_count}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Active</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 48 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: BLUE }}>{j.sent_count > 0 ? `${jobOpenRate}%` : '—'}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Opens</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 48 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#059669' }}>{j.sent_count > 0 ? `${jobClickRate}%` : '—'}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Clicks</div>
                    </div>
                    <div style={{ minWidth: 80 }}>
                      <div style={{ height: 5, background: '#f0f5fb', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, pct(j.active_count, j.active_count + j.completed_count + 1))}%`, height: '100%', background: '#22c55e', borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>Step 1–{j.total_steps || '?'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Deliverability Health */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Deliverability Health</span>
              <Badge color={bounceRate === 0 && kpi.total_spam === 0 ? '#059669' : '#b45309'}>
                {bounceRate === 0 && kpi.total_spam === 0 ? '✓ Healthy' : '⚠ Review'}
              </Badge>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Delivery Rate',       val: deliverRate,  color: '#22c55e' },
                { label: 'Open Rate',           val: openRate,     color: BLUE },
                { label: 'Click Rate',          val: clickRate,    color: ORANGE },
                { label: 'Bounce Rate',         val: bounceRate,   color: '#dc2626' },
                { label: 'Spam Complaint Rate', val: pct(kpi.total_spam, kpi.total_sent), color: '#dc2626' },
                { label: 'Unsubscribe Rate',    val: pct(kpi.total_unsubs, kpi.total_sent), color: '#b45309' },
              ].map(({ label, val, color }) => (
                <div key={label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{val}%</span>
                  </div>
                  <div style={{ height: 8, background: '#f0f5fb', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, val)}%`, height: '100%', background: color, borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Row 4: Template Performance ── */}
        <div style={{ ...s.card, marginBottom: 18 }}>
          <div style={s.cardHead}>
            <span style={s.cardTitle}>Email Performance by Template</span>
            <Link to="/admin/b2b/templates" style={s.linkBtn}>View templates →</Link>
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                {['#', 'Subject', 'Stream', 'Step', 'Sent', 'Opens', 'Open Rate', 'Clicks', 'Click Rate', 'Issues'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(templates || []).length === 0 && (
                <tr><td colSpan={10} style={s.empty}>No data yet</td></tr>
              )}
              {(templates || []).filter(t => t.sent > 0 || t.step_number).sort((a, b) => (b.sent || 0) - (a.sent || 0)).map((t, i) => (
                <tr key={t.template_id} style={s.tr}>
                  <td style={{ ...s.td, color: i < 3 ? ORANGE : '#94a3b8', fontWeight: 700, width: 28 }}>{i + 1}</td>
                  <td style={s.td}>
                    <div style={{ fontWeight: 500, color: NAVY, fontSize: 13 }}>{t.subject || t.template_name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.template_name}</div>
                  </td>
                  <td style={s.td}><Badge>{t.stream_name || '—'}</Badge></td>
                  <td style={{ ...s.td, color: '#64748b' }}>Step {t.step_number || '—'}</td>
                  <td style={{ ...s.td, fontWeight: 600 }}>{t.sent || 0}</td>
                  <td style={s.td}>{t.opens || 0}</td>
                  <td style={s.td}>
                    {t.sent > 0
                      ? <RateBar value={pct(t.opens, t.sent)} color={BLUE} />
                      : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                  </td>
                  <td style={s.td}>{t.clicks || 0}</td>
                  <td style={s.td}>
                    {t.sent > 0
                      ? <RateBar value={pct(t.clicks, t.sent)} color={ORANGE} />
                      : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                  </td>
                  <td style={s.td}>
                    {(t.bounces || 0) + (t.spam || 0) > 0
                      ? <Badge color="#dc2626">{(t.bounces || 0) + (t.spam || 0)} issues</Badge>
                      : <Badge color="#059669">✓ Clean</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Row 5: Most Engaged Contacts + Companies ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

          {/* Most Engaged Contacts */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Most Engaged Contacts</span>
              <Link to="/admin/b2b/contacts" style={s.linkBtn}>All contacts →</Link>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  {['#', 'Contact', 'Step', 'Opens', 'Clicks', 'Last Activity'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(contacts || []).length === 0 && <tr><td colSpan={6} style={s.empty}>No engagement data yet</td></tr>}
                {(contacts || []).slice(0, 10).map((c, i) => (
                  <tr key={c.contact_id} style={s.tr}>
                    <td style={{ ...s.td, color: i < 3 ? ORANGE : '#94a3b8', fontWeight: 700, width: 28 }}>{i + 1}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: 7, background: avatarColor(c.contact_id),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                        }}>{initials(c.first_name, c.last_name, c.company_name)}</div>
                        <div>
                          <Link to={`/admin/b2b/contacts/${c.contact_id}`} style={{ fontWeight: 600, color: NAVY, textDecoration: 'none', fontSize: 13 }}>
                            {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                          </Link>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.company_name || c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={s.td}>
                      {c.current_step ? <Badge>Step {c.current_step}</Badge> : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ ...s.td, fontWeight: 700, color: BLUE }}>{c.opens}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: ORANGE }}>{c.clicks}</td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 11 }}>{fmtDate(c.last_event_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Most Engaged Companies */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Most Engaged Companies</span>
              <Link to="/admin/b2b/accounts" style={s.linkBtn}>All accounts →</Link>
            </div>
            <table style={s.table}>
              <thead>
                <tr>
                  {['#', 'Company', 'Contacts', 'Opens', 'Clicks', 'Last Activity'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(companies || []).length === 0 && <tr><td colSpan={6} style={s.empty}>No engagement data yet</td></tr>}
                {(companies || []).slice(0, 10).map((co, i) => (
                  <tr key={co.account_id} style={s.tr}>
                    <td style={{ ...s.td, color: i < 3 ? ORANGE : '#94a3b8', fontWeight: 700, width: 28 }}>{i + 1}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: 7, background: avatarColor(co.account_id),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                        }}>{initials(null, null, co.company_name)}</div>
                        <div>
                          <Link to={`/admin/b2b/accounts/${co.account_id}`} style={{ fontWeight: 600, color: NAVY, textDecoration: 'none', fontSize: 13 }}>
                            {co.company_name}
                          </Link>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{[co.standard_industry, co.state].filter(Boolean).join(' · ')}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...s.td, color: '#64748b' }}>{co.contact_count}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: BLUE }}>{co.opens}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: ORANGE }}>{co.clicks}</td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 11 }}>{fmtDate(co.last_event_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Row 6: Enrollment Pipeline + Hot Leads ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

          {/* Enrollment Pipeline */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Enrollment Pipeline</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>{kpi.active_contacts} active contacts</span>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: maxStep }, (_, i) => i + 1).map(step => {
                // Find sent count for this step from templates
                const stepTemplates = (templates || []).filter(t => t.step_number === step);
                const stepSent = stepTemplates.reduce((sum, t) => sum + (t.sent || 0), 0);
                const stepOpen = stepTemplates.reduce((sum, t) => sum + (t.opens || 0), 0);
                const inStep   = pipeMap[step] || 0;
                const maxSent  = Math.max(...(templates || []).map(t => t.sent || 0), 1);
                return (
                  <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 48, fontSize: 12, fontWeight: 600, color: '#64748b', flexShrink: 0 }}>Step {step}</div>
                    <div style={{ flex: 1, height: 26, background: '#f0f5fb', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                      <div style={{
                        width: `${Math.max(4, Math.round((stepSent / maxSent) * 100))}%`,
                        height: '100%',
                        background: step === 1 ? BLUE : step === 2 ? '#0891b2' : step === 3 ? ORANGE : step === 4 ? '#7c3aed' : '#94a3b8',
                        borderRadius: 6, display: 'flex', alignItems: 'center', paddingLeft: 8,
                      }}>
                        {stepSent > 0 && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {stepSent} sent · {pct(stepOpen, stepSent)}% open
                        </span>}
                      </div>
                    </div>
                    <div style={{ width: 28, textAlign: 'right', fontSize: 13, fontWeight: 700, color: stepSent > 0 ? NAVY : '#94a3b8' }}>{stepSent || 0}</div>
                    {inStep > 0 && <div style={{ fontSize: 11, color: '#059669', fontWeight: 600, whiteSpace: 'nowrap' }}>({inStep} active)</div>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, padding: '12px 20px', borderTop: '1px solid #f1f5f9' }}>
              {[
                { label: 'Active',    val: kpi.active_contacts,    color: BLUE },
                { label: 'Completed', val: kpi.completed_contacts, color: '#059669' },
                { label: 'Halted',    val: kpi.halted_contacts,    color: '#b45309' },
                { label: 'Unsub\'d',  val: kpi.total_unsubs,       color: '#dc2626' },
              ].map(({ label, val, color }) => (
                <div key={label} style={{ textAlign: 'center', padding: '8px', background: '#f8fafc', borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color }}>{val ?? 0}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hot Leads */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={s.cardTitle}>🔥 Hot Leads</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Clicked 3+ times</span>
              </div>
              {hotLeads.length > 0 && <Badge color={ORANGE}>{hotLeads.length} flagged</Badge>}
            </div>
            <div style={{ padding: hotLeads.length === 0 ? 0 : '8px 0' }}>
              {hotLeads.length === 0 && (
                <div style={{ ...s.empty, padding: '32px 20px' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>👀</div>
                  No hot leads yet — contacts with 3+ clicks will appear here
                </div>
              )}
              {hotLeads.map(c => (
                <div key={c.contact_id} style={{ margin: '8px 20px', padding: '14px', background: '#fff7ed', borderRadius: 10, border: '1px solid #fed7aa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>
                        {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                        {c.company_name && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}> · {c.company_name}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{c.standard_title || c.title} · {c.email}</div>
                    </div>
                    <Badge color={ORANGE}>{c.clicks} clicks</Badge>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: '#64748b' }}>
                    <span>👀 {c.opens} opens</span>
                    <span>👆 {c.clicks} clicks</span>
                    {c.current_step && <span>📧 Step {c.current_step}</span>}
                    <span>🕐 {fmtDate(c.last_event_at)}</span>
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <Link to={`/admin/b2b/contacts/${c.contact_id}`}
                      style={{ ...s.primaryBtn, textDecoration: 'none', padding: '5px 14px', fontSize: 12 }}>
                      View Contact
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Row 7: Upcoming Sends + Recent Activity ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

          {/* Upcoming Sends */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Upcoming Sends (48h)</span>
              <Badge color={BLUE}>{(schedule || []).length} queued</Badge>
            </div>
            <table style={s.table}>
              <thead>
                <tr>{['Scheduled (UTC)', 'Contact', 'Step', 'Template'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {(schedule || []).length === 0 && <tr><td colSpan={4} style={s.empty}>No sends in the next 48 hours</td></tr>}
                {(schedule || []).map(r => (
                  <tr key={r.enrollment_id} style={s.tr}>
                    <td style={{ ...s.td, fontSize: 12, whiteSpace: 'nowrap', color: '#374151' }}>
                      <strong>{fmtDate(r.next_send_utc)}</strong>
                    </td>
                    <td style={s.td}>
                      <Link to={`/admin/b2b/contacts/${r.contact_id}`} style={{ color: NAVY, textDecoration: 'none', fontWeight: 500, fontSize: 13 }}>
                        {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                      </Link>
                      {r.company_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.company_name}</div>}
                    </td>
                    <td style={s.td}><Badge color={r.current_step === 1 ? '#059669' : BLUE}>Step {r.current_step}</Badge></td>
                    <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.subject}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent Activity */}
          <div style={s.card}>
            <div style={s.cardHead}>
              <span style={s.cardTitle}>Recent Activity</span>
              <Link to="/admin/dash" style={s.linkBtn}>View full log →</Link>
            </div>
            <div>
              {(activity || []).length === 0 && <div style={s.empty}>No recent events</div>}
              {(activity || []).map((ev, i) => {
                const icon = ev.event_type === 'open'  ? '👀' :
                             ev.event_type === 'click' ? '👆' :
                             ev.event_type === 'bounce' || ev.event_type === 'out_of_band' ? '⚠️' :
                             ev.event_type === 'spam_complaint' ? '🚫' :
                             ev.event_type === 'delivery' ? '✉️' : '📬';
                const bg = ev.event_type === 'click' ? '#dbeafe' :
                           ev.event_type === 'open'  ? '#f0fdf4' :
                           (ev.event_type === 'bounce' || ev.event_type === 'spam_complaint') ? '#fee2e2' : '#f8fafc';
                return (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 20px', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                      {icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>
                        {[ev.first_name, ev.last_name].filter(Boolean).join(' ')} · <span style={{ fontWeight: 400, textTransform: 'capitalize' }}>{ev.event_type?.replace('_', ' ')}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        {ev.subject}
                        {ev.event_url && <span> · <a href={ev.event_url} target="_blank" rel="noreferrer" style={{ color: BLUE }}>link</a></span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtDate(ev.occurred_on_utc)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" },
  main:       { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  title:      { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardHead:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #e2e8f0' },
  cardTitle:  { color: NAVY, fontWeight: 600, fontSize: 14 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '9px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty:      { color: '#94a3b8', fontSize: 13, padding: '28px', textAlign: 'center' },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  outlineBtn: { background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  linkBtn:    { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, textDecoration: 'none' },
};
